import { ActionIcon, AppShell, Avatar, Box, Burger, Group, Menu, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconBuilding,
  IconBuildingCommunity,
  IconChevronsLeft,
  IconChevronsRight,
  IconClipboardList,
  IconDatabase,
  IconForms,
  IconLayoutDashboard,
  IconLogout,
  IconRosette,
  IconUserCircle,
} from '@tabler/icons-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { tr, uiDirection } from '../lib/i18n';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { ErrorBoundary } from './ErrorBoundary';
import { LanguageButton } from './LanguageButton';
import { PageActionsProvider, PageActionsSlot } from './page-actions';
import { PlatformLogo } from './platform-logo';

const NAV_W = 248;
const RAIL_W = 68;
const HEADER_H = 56;
const COLLAPSE_KEY = 'platform.navCollapsed';

type NavItem = { to: string; label: string; icon: ReactNode };
const ICON = { size: 19, stroke: 1.6 };
const NAV: { label?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/dashboard', label: tr('اللوحة الرئيسية', 'Dashboard'), icon: <IconLayoutDashboard {...ICON} /> },
      { to: '/organizations', label: tr('الشركات', 'Companies'), icon: <IconBuilding {...ICON} /> },
      { to: '/plans', label: tr('الباقات', 'Plans'), icon: <IconRosette {...ICON} /> },
    ],
  },
  {
    label: tr('النموذج والبيانات', 'Form & data'),
    items: [
      { to: '/forms', label: tr('منشئ النموذج', 'Form builder'), icon: <IconForms {...ICON} /> },
      { to: '/reference-data', label: tr('البيانات المرجعية', 'Reference data'), icon: <IconDatabase {...ICON} /> },
    ],
  },
  {
    label: tr('نظرة عامة', 'Overview'),
    items: [
      { to: '/overview/properties', label: tr('كل العقارات', 'All properties'), icon: <IconBuildingCommunity {...ICON} /> },
      { to: '/overview/requests', label: tr('كل الطلبات', 'All requests'), icon: <IconClipboardList {...ICON} /> },
    ],
  },
];

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

function Item({ it, rail, onNavigate }: { it: NavItem; rail: boolean; onNavigate?: () => void }) {
  const active = !!useMatch({ path: it.to, end: false });
  const button = (
    <UnstyledButton
      component={NavLink}
      to={it.to}
      onClick={onNavigate}
      aria-label={rail ? it.label : undefined}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 36,
        paddingInline: rail ? 0 : 10,
        justifyContent: rail ? 'center' : 'flex-start',
        borderRadius: 8,
        position: 'relative',
        color: active ? 'var(--pf-text)' : 'var(--pf-muted)',
        background: active ? 'var(--pf-selected)' : undefined,
        fontWeight: active ? 600 : 500,
        fontSize: 13.5,
        transition: 'background-color 120ms, color 120ms',
      }}
      className="pf-nav-item"
    >
      {active && <Box pos="absolute" top={8} bottom={8} w={3} bg="ink.9" style={{ insetInlineStart: rail ? 4 : -12, borderRadius: 3 }} />}
      <Box component="span" style={{ display: 'grid', placeItems: 'center', color: active ? 'var(--pf-text)' : 'var(--pf-faint)' }}>{it.icon}</Box>
      {!rail && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>}
    </UnstyledButton>
  );
  return rail ? <Tooltip label={it.label} position={uiDirection() === 'rtl' ? 'left' : 'right'}>{button}</Tooltip> : button;
}

function Nav({ rail, onNavigate }: { rail: boolean; onNavigate?: () => void }) {
  return (
    <Stack gap="lg" component="nav" aria-label={tr('القائمة الرئيسية', 'Main navigation')}>
      {NAV.map((g, gi) => (
        <Stack key={gi} gap={2}>
          {g.label && (rail ? <Box h={1} mx={8} mb={6} bg="var(--pf-line)" /> : <Text size="xs" fw={500} c="var(--pf-faint)" px={10} mb={4}>{g.label}</Text>)}
          {g.items.map((it) => <Item key={it.to} it={it} rail={rail} onNavigate={onNavigate} />)}
        </Stack>
      ))}
    </Stack>
  );
}

function AccountMenu() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  return (
    <Menu position="bottom-end" width={230}>
      <Menu.Target>
        <UnstyledButton aria-label={tr('الحساب', 'Account')} style={{ borderRadius: 99 }}>
          <Avatar size={32} radius="xl" color="ink" variant="filled">{me?.fullName?.[0] ?? 'F'}</Avatar>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Box px="sm" py={8}>
          <Text size="sm" fw={600}>{me?.fullName}</Text>
          <Text size="xs" c="dimmed" dir="ltr" ta="start">{me?.email}</Text>
        </Box>
        <Menu.Divider />
        <Menu.Item leftSection={<IconUserCircle size={16} />} onClick={() => nav('/profile')}>{tr('الحساب وكلمة المرور', 'Account & password')}</Menu.Item>
        <Menu.Item leftSection={<IconLogout size={16} />} color="red" onClick={() => void logout()}>{tr('تسجيل الخروج', 'Sign out')}</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function Layout() {
  const desktop = useMediaQuery('(min-width: 62em)', true);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, mobile] = useDisclosure(false);
  const { pathname } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);
  // the form builder uses the whole screen and scrolls inside its own panes
  const fullBleed = /^\/forms\/[^/]+$/.test(pathname);
  const rail = desktop && collapsed;

  useEffect(() => {
    scroller.current?.scrollTo(0, 0);
    mobile.close();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRail = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  const CollapseIcon = (uiDirection() === 'rtl') === collapsed ? IconChevronsLeft : IconChevronsRight;

  return (
    <PageActionsProvider>
      <AppShell
        header={{ height: HEADER_H }}
        navbar={{ width: rail ? RAIL_W : NAV_W, breakpoint: 'md', collapsed: { mobile: !mobileOpen } }}
        padding={0}
        styles={{
          header: { background: 'var(--pf-surface)', borderColor: 'var(--pf-line)' },
          navbar: { background: 'var(--pf-surface)', borderColor: 'var(--pf-line)', transition: 'width 180ms ease' },
          main: { background: 'var(--pf-canvas)' },
        }}
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Burger opened={mobileOpen} onClick={mobile.toggle} hiddenFrom="md" size="sm" aria-label={tr('القائمة الرئيسية', 'Main navigation')} />
              <PlatformLogo size={28} />
              <Text fw={600} size="md" style={{ whiteSpace: 'nowrap' }}>FormTags</Text>
              <Text size="xs" fw={500} c="dimmed" px={8} py={2} style={{ border: '1px solid var(--pf-line)', borderRadius: 6 }} visibleFrom="xs">
                {tr('لوحة المنصة', 'Platform console')}
              </Text>
            </Group>
            <Group gap={6} wrap="nowrap">
              <LanguageButton compact />
              <AccountMenu />
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p={rail ? 8 : 'sm'} pt="md">
          <AppShell.Section grow component={ScrollArea} type="never">
            <Box px={rail ? 0 : 4}>
              <Nav rail={rail} onNavigate={mobile.close} />
            </Box>
          </AppShell.Section>
          <AppShell.Section visibleFrom="md" pt="xs" style={{ borderTop: '1px solid var(--pf-line)' }}>
            <Tooltip label={collapsed ? tr('توسيع القائمة', 'Expand menu') : tr('طي القائمة', 'Collapse menu')} position={uiDirection() === 'rtl' ? 'left' : 'right'}>
              <ActionIcon onClick={toggleRail} size="lg" mx={rail ? 'auto' : 4} display="flex" aria-label={tr('طي القائمة', 'Collapse menu')}>
                <CollapseIcon size={18} stroke={1.6} />
              </ActionIcon>
            </Tooltip>
          </AppShell.Section>
        </AppShell.Navbar>

        <AppShell.Main>
          <Box
            ref={scroller}
            style={{ height: `calc(100dvh - ${HEADER_H}px)`, overflowY: fullBleed ? 'hidden' : 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}
            px={{ base: 'sm', md: fullBleed ? 'md' : 'xl' }}
            pb={fullBleed ? 0 : 'xl'}
          >
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm" pt={fullBleed ? 'sm' : 'lg'} pb={fullBleed ? 'sm' : 'lg'} style={{ flexShrink: 0 }}>
              <AppBreadcrumbs />
              <PageActionsSlot />
            </Group>
            <Box style={fullBleed ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { flex: '1 0 auto' }}>
              <ErrorBoundary resetKey={pathname}>
                <Outlet />
              </ErrorBoundary>
            </Box>
          </Box>
        </AppShell.Main>
      </AppShell>
    </PageActionsProvider>
  );
}
